import { CommonModule } from '@angular/common';
import { Component, Injector, OnInit, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { NgxPaginationModule } from 'ngx-pagination';
import {DEBOUNCE_TIME, extractErrorMessage, goToNextPage} from 'src/app/common/common';
import { MarketContextViewModel } from 'src/app/model/market-context.model';
import { AuthService } from 'src/app/services/auth.service';
import {ApiService, CustomHttpParams} from 'src/app/services/api.service';
import {PaginationClass, PaginationModel} from "../../../../common/pagination";
import {LoaderComponent} from "../../../common/loader/loader.component";
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-market-context-index',
  standalone: true,
  imports: [RouterModule, CommonModule, FormsModule, NgxPaginationModule, LoaderComponent],
  templateUrl: './market-context-index.component.html',
  styleUrl: './market-context-index.component.scss'
})
export class MarketContextIndexComponent implements OnInit, OnDestroy {
  // Search fields for all columns
  public idSearch: string = '';
  public marketContextUriSearch: string = '';
  public operatorSearch: string = '';
  public programSearch: string = '';
  public productSearch: string = '';
  public labelSearch: string = '';
  public updatedBySearch: string = '';

  // Debounce search using BehaviorSubject
  private searchParams$ = new BehaviorSubject<any>({
    id: '',
    marketContextUri: '',
    operator: '',
    program: '',
    product: '',
    eventsCount: '',
    vensCount: '',
    groupsCount: '',
    label: '',
    updatedBy: ''
  });
  private unsubscribe = new Subject<void>();

  protected readonly paginationId = 'marketContextsPagination';
  private pagination = new PaginationClass(this.paginationId);
  protected paginationModel: PaginationModel = this.pagination.getPaginationModel();

  public isAdmin: boolean = false;
  public isLoading: boolean = false;
  public error: string | null = null;
  public displayedMarketContexts: MarketContextViewModel[] = [];
  public router: Router;

  constructor(
    private injector: Injector,
    private authService: AuthService,
    private marketContextService: ApiService
  ) {
    this.router = injector.get(Router);
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.isAdmin();
    this.paginationModel = this.pagination.resetPagination();

    // Setup debounced search that tracks actual parameter changes
    this.searchParams$.pipe(
      debounceTime(DEBOUNCE_TIME),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      takeUntil(this.unsubscribe)
    ).subscribe(() => {
      this.loadMarketContexts();
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  buildSearchParams(): CustomHttpParams {
    const params: CustomHttpParams = {
      'depth': 2,
      'limit': this.pagination.itemsPerPage,
      'offset': this.pagination.offset,
    };

    // Add search parameters if they exist
    if (this.idSearch) {
      params['id'] = this.idSearch.trim();
    }
    if (this.marketContextUriSearch) {
      params['market_context'] = this.marketContextUriSearch.trim();
    }
    if (this.operatorSearch) {
      params['operator'] = this.operatorSearch.trim();
    }
    if (this.programSearch) {
      params['program'] = this.programSearch.trim();
    }
    if (this.productSearch) {
      params['product'] = this.productSearch.trim();
    }
    if (this.labelSearch) {
      params['label'] = this.labelSearch.trim();
    }
    if (this.updatedBySearch) {
      params['updated_by'] = this.updatedBySearch.trim();
    }
    return params;
  }

  triggerSearch() {
    this.paginationModel = this.pagination.resetPagination();

    // Update the search parameters subject with current values
    this.searchParams$.next({
      id: this.idSearch,
      marketContextUri: this.marketContextUriSearch,
      operator: this.operatorSearch,
      program: this.programSearch,
      product: this.productSearch,
      label: this.labelSearch,
      updatedBy: this.updatedBySearch
    });
  }

  loadMarketContexts() {
    this.isLoading = true;
    this.error = null;

    // Build parameters using the centralized method
    const queryParams = this.buildSearchParams();
    this.marketContextService.getMarketContext(queryParams)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response: any) => {
          this.displayedMarketContexts = response.map((marketContext: MarketContextViewModel) => marketContext);
          this.paginationModel = this.pagination.updateTotalItems(this.displayedMarketContexts.length);
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Error loading market contexts:', err);
          this.error = extractErrorMessage(err, "Failed to load market contexts.");
          this.isLoading = false;
        }
      });
  }

  handlePageChange(newPage: number) {
    this.paginationModel = this.pagination.changePage(newPage);
    this.loadMarketContexts();
  }

  nextPage(event: any, url: string, router: Router) {
    return goToNextPage(event, url, router);
  }

  delete(marketContext: any) {
    if (confirm('Are you sure you want to delete this market context?')) {
      this.marketContextService.deleteMarketContext(marketContext.id)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: () => {
            this.loadMarketContexts(); // Refresh list after deletion
          },
          error: (err) => {
            console.error('Error deleting market context:', err);
            this.error = extractErrorMessage(err, "Failed to delete market context.");
          }
        });
    }
  }
}
